import React, { createContext, useContext, useState, useEffect } from 'react';
import { CookieConsentStatus, CookiePreferences } from '../types/cookies';

interface CookieConsentContextType {
  consentStatus: CookieConsentStatus | null;
  updateConsent: (preferences: CookiePreferences) => void;
  acceptAll: () => void;
  rejectAll: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const STORAGE_KEY = 'cookie-consent-status';
const COOKIE_VERSION = '1.0.0'; // Increment when cookie structure changes

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consentStatus, setConsentStatus] = useState<CookieConsentStatus | null>(null);

  useEffect(() => {
    const storedConsent = localStorage.getItem(STORAGE_KEY);
    if (storedConsent) {
      try {
        const parsed = JSON.parse(storedConsent);
        if (parsed.version === COOKIE_VERSION) {
          setConsentStatus(parsed.data);
        }
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
      }
    }
  }, []);

  const saveConsent = (newStatus: CookieConsentStatus) => {
    setConsentStatus(newStatus);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: COOKIE_VERSION,
      data: newStatus
    }));
  };

  const updateConsent = (preferences: CookiePreferences) => {
    saveConsent({
      hasConsented: true,
      preferences,
      lastUpdated: new Date().toISOString()
    });
  };

  const acceptAll = () => {
    const allPreferences: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    updateConsent(allPreferences);
  };

  const rejectAll = () => {
    const minimalPreferences: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false
    };
    updateConsent(minimalPreferences);
  };

  return (
    <CookieConsentContext.Provider value={{ consentStatus, updateConsent, acceptAll, rejectAll }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};